/** @jsxImportSource @emotion/react */
/* eslint-disable react/prop-types */
import React from 'react';
import { Link } from 'react-router-dom';
import { Flex } from 'rebass';
import { SwitchStyled, SwitchOptionStyled, useTokens } from '@nike/nike-design-system-components';
import { Plus } from '@nike/nike-design-system-icons';

import { StyledExclamationMark } from '../../../components/icons';
import BadgeStyled from '../../../components/BadgeStyled';
import ColumnFilterSort from '../../../components/tableServerSide/ColumnFilterSort';

import { formatDateTimestamp } from '../../../util/date';

const getIsEnabledAutoAssignedValue = input => {
  switch (input?.toLowerCase()) {
    case 'yes':
      return 'true';
    case 'no':
      return 'false';
    default:
      return input;
  }
};

export const columnSortModifiersAccountContracts = {
  sourceContractId: null,
  group: { disabled: true },
  poNumber: null,
  beginDate: null,
  endDate: null,
  isEnabledAutoAssigned: { disabled: true },
  defaultSort: {}
};

export const columnFilterModifiersAccountContracts = {
  group: { expression: 'Like', colId: 'contractGroupName', value: value => value.name },
  poNumber: { expression: 'Like' },
  beginDate: { disabled: true },
  endDate: { disabled: true },
  isEnabledAutoAssigned: { expression: '', value: input => getIsEnabledAutoAssignedValue(input) },
  defaultSort: {},
  departmentName: { expression: 'Like' }
};

export const getAccountContractColumns = ({
  handleOpenAddDepartmentModal,
  handleOpenRemoveDepartmentModal,
  toggleAutoAssignedContract
}) => {
  const tokens = useTokens();

  const DepartmentCell = ({ column, contract }) => (
    <div css={{ display: 'flex', width: '100%', height: '100%', overflowY: 'scroll', justifyContent: 'space-between' }}>
      <div css={{ width: '135px' }}>
        {contract.departments?.map(department => (
          <BadgeStyled
            text={department[column]}
            onClose={() =>
              handleOpenRemoveDepartmentModal({
                contractNumber: contract.sourceContractId,
                contractId: contract.id,
                departmentNumber: department.number,
                departmentName: department.name
              })
            }
            backgroundColor={tokens.podiumCdsColorGrey200}
            width='100%'
          />
        ))}
      </div>
      <div
        onClick={() =>
          handleOpenAddDepartmentModal({
            contractNumber: contract.sourceContractId,
            contractId: contract.id
          })
        }
        css={{ cursor: 'pointer', justifyContent: 'flex-end', height: '100%' }}
      >
        <Plus size='m' data-testid='addDepartmentNumber' />
      </div>
    </div>
  );

  return [
    {
      Header: tableInstance => (
        <ColumnFilterSort header='Contract Number' tableInstance={tableInstance} align='right' />
      ),
      accessor: 'sourceContractId',
      width: 150
    },
    {
      Header: tableInstance => <ColumnFilterSort header='Contract Group Name' tableInstance={tableInstance} />,
      accessor: 'group',
      width: 350,
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) =>
        value ? (
          <Link
            to={`/productContractGroups/edit/${value.id}`} // eslint-disable-line react/prop-types
            style={{ color: '#3498db', textDecoration: 'underline' }}
          >
            {
              value.name // eslint-disable-line react/prop-types
            }
          </Link>
        ) : null
    },
    {
      Header: tableInstance => <ColumnFilterSort header='PO Number' tableInstance={tableInstance} />,
      accessor: 'poNumber',
      width: 350
    },
    {
      Header: tableInstance => <ColumnFilterSort header='Begin Date' tableInstance={tableInstance} />,
      accessor: 'beginDate',
      width: 120,
      Cell: ({ value }) => formatDateTimestamp(value)
    },
    {
      Header: tableInstance => <ColumnFilterSort header='End Date' tableInstance={tableInstance} />,
      accessor: 'endDate',
      width: 120,
      Cell: ({ value }) => formatDateTimestamp(value)
    },
    {
      Header: 'Exp',
      id: 'exp',
      accessor: 'endDate',
      width: 120,
      // eslint-disable-next-line react/prop-types
      Cell: ({ value }) => {
        const daysPrior = 10;
        const expirationDate = new Date(value);
        const thresholdDate = new Date(expirationDate.getTime() - daysPrior * 24 * 60 * 60 * 1000);

        return (
          new Date() > thresholdDate && (
            <Flex justifyContent='center' width='100%'>
              <StyledExclamationMark data-testid='exp-excl' />
            </Flex>
          )
        );
      }
    },
    {
      Header: tableInstance => (
        <ColumnFilterSort header='Department Name' tableInstance={tableInstance} align='right' />
      ),
      accessor: 'departmentName',
      width: 200,
      Cell: ({ row }) => {
        return DepartmentCell({ column: 'name', contract: row.original });
      }
    },
    {
      Header: tableInstance => (
        <ColumnFilterSort header='Department Number' tableInstance={tableInstance} align='right' />
      ),
      accessor: 'departmentNumber',
      width: 200,
      Cell: ({ row }) => {
        return DepartmentCell({ column: 'number', contract: row.original });
      }
    },
    {
      Header: tableInstance => <ColumnFilterSort header='Avail on Gameday' tableInstance={tableInstance} />,
      accessor: 'isEnabledAutoAssigned',
      width: 200,
      Cell: ({ row }) =>
        row.original.isEnabledAutoAssigned !== null && (
          <Flex justifyContent='center' width='100%'>
            <SwitchStyled
              selectedValue={row.original.isEnabledAutoAssigned ? 'enable' : 'disable'}
              name='contractEnabledToggle'
              onClick={(e, value) =>
                toggleAutoAssignedContract({
                  toggle: value,
                  contractId: row.original.id
                })
              }
            >
              <SwitchOptionStyled id='switch-contract-off' label='NO' value='disable' />
              <SwitchOptionStyled id='switch-contract-on' label='YES' value='enable' />
            </SwitchStyled>
          </Flex>
        )
    }
  ];
};
